import React, { FC, useCallback, useState } from 'react';

import { useAddAuthorizedCustomer, useUpdateAuthorizedCustomers } from '../../fetchHooks/useAuthorizedCustomers';
import type { AccountId } from '../../generated-types';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { accountTrackingIDs } from '../../utils/gtm/trackIDs/account';
import { EikaModal } from '../EikaModal';
import AddAuthorizedCustomerForm, { ANALYTICS_CATEGORY } from './AddAuthorizedCustomerForm';

import './AddAuthorizedCustomerModal.scss';

interface Props {
    show: boolean;
    accountId: AccountId;
    setShow: React.Dispatch<boolean>;
}

const AddAuthorizedCustomerModal: FC<Props> = ({ show, accountId, setShow }) => {
    const [customerSsid, setCustomerSsid] = useState<string>('');
    const [customerName, setCustomerName] = useState<string>('');
    const refreshAuthorizedCustomers = useUpdateAuthorizedCustomers();

    const addAuthorizedCustomer = useAddAuthorizedCustomer(accountId);

    const createAddAuthorizedCustomerRequest = () => {
        return addAuthorizedCustomer.mutateAsync({
            customerNumber: { value: customerSsid },
            customerName,
            deviceId: window.ReactNativeProps?.deviceId,
        });
    };

    const { signingStatus, handleCancelSigningFlow, startSigning } = useSigningContext(
        createAddAuthorizedCustomerRequest,
    );

    const closeAndCancelSigning = useCallback(() => {
        handleCancelSigningFlow();
        setShow(false);
    }, [handleCancelSigningFlow, setShow]);

    const onSetCustomerSsid = useCallback((value: string) => {
        setCustomerSsid(value.replaceAll(' ', ''));
    }, []);

    const closeModal = useCallback(() => {
        setCustomerSsid('');
        setCustomerName('');
        addAuthorizedCustomer.reset();
        if (signingStatus && signingStatus === 'COMPLETED') {
            refreshAuthorizedCustomers();
        }
        closeAndCancelSigning();
        setShow(false);
    }, [setShow, addAuthorizedCustomer, closeAndCancelSigning, signingStatus, refreshAuthorizedCustomers]);

    const submit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            startSigning();
        },
        [startSigning],
    );

    return (
        <EikaModal
            id="addAuthorizedCustomer"
            className="add-authorized-customer-modal"
            open={show}
            setOpen={setShow}
            onClose={closeModal}
            contentShouldStretch
            appNamespace="dagligbank-lokalbank-web"
            dataTrackName={accountTrackingIDs.addAuthorizedCustomer.name}
        >
            <AddAuthorizedCustomerForm
                accountId={accountId}
                setCustomerSsid={onSetCustomerSsid}
                setCustomerName={setCustomerName}
                onSubmit={submit}
                customerName={customerName}
                customerSsid={customerSsid}
                addAuthorizedCustomerLoading={addAuthorizedCustomer.isPending}
                addAuthorizedCustomerError={addAuthorizedCustomer.error || undefined}
                onClose={closeModal}
            />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORY}>
        <AddAuthorizedCustomerModal {...props} />
    </SigningContextProvider>
);
